import React from 'react';
import { DownloadSimple, ShieldCheck, SpinnerGap, FloppyDisk } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { LANGUAGES } from '../../services/translationService';

export default function SettingsTab({ settings, setSettings, savingSettings, handleSaveSettings, handleExportData }) {
  const checkbox = (key, label) => (
    <label className="flex items-center space-x-3">
      <input
        type="checkbox"
        checked={settings[key]}
        onChange={(e) => setSettings({ ...settings, [key]: e.target.checked })}
        className="w-4 h-4 text-blue-600"
      />
      <span className="text-gray-700">{label}</span>
    </label>
  );

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Account Settings</h2>
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Job Preferences</h3>
            <div className="space-y-3">
              {checkbox('email_notifications', 'Email me new job matches')}
              {checkbox('sms_alerts', 'SMS alerts for urgent jobs')}
              {checkbox('weekly_digest', 'Weekly job digest')}
              {checkbox('application_updates', 'Application status updates')}
            </div>
          </div>

          <div className="pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Privacy Settings</h3>
            <div className="space-y-3">
              {checkbox('profile_visible', 'Profile visible to contractors')}
              {checkbox('show_contact_public', 'Show contact info publicly')}
              {checkbox('allow_messages', 'Allow direct messages')}
              {checkbox('show_availability', 'Share availability status')}
            </div>
          </div>

          <div className="pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Language</h3>
            <p className="text-sm text-gray-500 mb-3">Chat messages translate to this language when you tap "Translate".</p>
            <select
              value={settings.preferred_language || 'en'}
              onChange={(e) => setSettings({ ...settings, preferred_language: e.target.value })}
              className="w-full h-11 px-3 border-2 border-gray-200 rounded-lg font-medium text-gray-900 bg-white"
              data-testid="preferred-language-select"
            >
              {Object.values(LANGUAGES).map((l) => (
                <option key={l.code} value={l.code}>{l.flag} {l.name}</option>
              ))}
            </select>
          </div>

          <div className="pt-6 border-t border-gray-200">
            <Button
              onClick={handleSaveSettings}
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={savingSettings}
              data-testid="save-settings-btn"
            >
              {savingSettings ? (
                <><SpinnerGap size={20} weight="bold" className="mr-2 animate-spin" />Saving...</>
              ) : (
                <><FloppyDisk size={16} weight="bold" className="mr-2" />Save Settings</>
              )}
            </Button>
          </div>

          <div className="pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Actions</h3>
            <div className="space-y-3">
              <Button onClick={handleExportData} variant="outline" className="w-full">
                <DownloadSimple size={16} weight="bold" className="mr-2" />
                Export My Data
              </Button>
              <Button variant="outline" className="w-full">
                <ShieldCheck size={16} weight="bold" className="mr-2" />
                Change Password
              </Button>
              <Button variant="outline" className="w-full text-red-600 hover:bg-red-50">
                Deactivate Account
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
